import styles from './styles.module.scss';
import clsx from 'clsx';
import { formatDateStringTimeUsWithTz } from '../../functions/format/formatDate';
import {
  IDocumentRequestEvent,
  RequestSourceEnum,
} from '../../common/event/interfaces/document-request-event';
import { DocumentRequestEventNameEnum } from '../../common/event/enums/document-request-event-name.enum';
import { generateDocumentRequestEventName } from 'common/event/ui/generate-document-request-event-name';
import { TextButton } from '../Buttons/TextButton';
import { useMemo, useState } from 'react';

type Align = 'center' | 'flex-start';

type LogInputProps = {
  log: IDocumentRequestEvent;
  align?: Align;
};

const Log = ({ log, align }: LogInputProps) => {
  let dotClass = styles.default;
  switch (log.eventName) {
    case DocumentRequestEventNameEnum.DOCUMENT_SEND_TO_SUBSCRIPTION_SUCCESS:
    case DocumentRequestEventNameEnum.DOCUMENT_UPLOADED:
      dotClass = styles.submitted;
      break;
    case DocumentRequestEventNameEnum.DOCUMENT_APPROVED_SMS_STATUS_DELIVERED:
    case DocumentRequestEventNameEnum.DOCUMENT_APPROVED:
      dotClass = styles.approved;
      break;
    case DocumentRequestEventNameEnum.DOCUMENT_REQUEST_SMS_STATUS_CHANGED:
    case DocumentRequestEventNameEnum.DOCUMENT_REQUEST_SMS_DELIVERED:
      dotClass = styles.approved;
      break;
    case DocumentRequestEventNameEnum.DOCUMENT_SEND_TO_SUBSCRIPTION_FAIL:
    case DocumentRequestEventNameEnum.DOCUMENT_REQUEST_SMS_ERROR:
    case DocumentRequestEventNameEnum.DOCUMENT_APPROVED_SMS_STATUS_FAILED:
      dotClass = styles.error;
      break;
  }

  return (
    <div className={styles.log} style={{ justifyContent: align }}>
      <div className={clsx(styles.dot, dotClass)}></div>
      <div className={styles.content}>
        <div>
          {log.eventDate && (
            <b className={styles.date}>
              {formatDateStringTimeUsWithTz(log.eventDate)} -{' '}
            </b>
          )}
        </div>
        <div>
          {log.documentName && <b>{log.documentName}</b>}
          {log.eventName && (
            <span> {generateDocumentRequestEventName(log)}</span>
          )}
          {log.source === RequestSourceEnum.DRIVER ? (
            <span>
              {' '}
              by {log.candidateUserName || log.triggeredByUserName || ''} (Self
              Serve)
            </span>
          ) : (
            log.triggeredByUserName && (
              <span> by {log.triggeredByUserName}</span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

type LogsInputProps = {
  logs: IDocumentRequestEvent[];
  align?: Align;
  hideLogsAfterRetentionPeriodDays?: number;
};

export const LogEvents = ({
  logs,
  align = 'flex-start',
  hideLogsAfterRetentionPeriodDays,
}: LogsInputProps) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const { lastLogs, countShowMore } = useMemo(() => {
    if (!logs) {
      return {
        lastLogs: [],
        countShowMore: 0,
      };
    }

    const MIN_VISIBLE_LOGS = 5;
    const minVisible = Math.min(MIN_VISIBLE_LOGS, logs.length);
    const lastLogs: IDocumentRequestEvent[] = [];
    for (const log of logs) {
      const isVisibleByRetention =
        !hideLogsAfterRetentionPeriodDays ||
        !log.eventDate ||
        isWithinRetentionPeriod(
          log.eventDate,
          hideLogsAfterRetentionPeriodDays
        );

      if (isVisibleByRetention || lastLogs.length < minVisible) {
        lastLogs.push(log);
      }
    }

    return {
      lastLogs,
      countShowMore: logs.length - lastLogs.length,
    };
  }, [logs, hideLogsAfterRetentionPeriodDays]);

  const visibleLogs = useMemo(() => {
    return showAll ? logs : lastLogs;
  }, [showAll, logs, lastLogs]);

  return (
    <div className={styles.logs}>
      {visibleLogs.map((log, index) => (
        <Log key={index} log={log} align={align} />
      ))}
      {countShowMore > 0 &&
        (showAll ? (
          <TextButton
            className={styles.button}
            onClick={() => setShowAll(false)}
          >
            Show Less
          </TextButton>
        ) : (
          <TextButton
            className={styles.button}
            onClick={() => setShowAll(true)}
          >
            Show More ({countShowMore})
          </TextButton>
        ))}
    </div>
  );
};

function isWithinRetentionPeriod(
  eventDate: string,
  hideLogsAfterRetentionPeriodDays: number
) {
  const currentDate = new Date();
  const eventDateObj = new Date(eventDate);
  const timeDifference = currentDate.getTime() - eventDateObj.getTime();
  const daysDifference = timeDifference / (1000 * 3600 * 24);
  return daysDifference <= hideLogsAfterRetentionPeriodDays;
}
